import { computed, defineComponent, PropType, toRaw } from "vue";
import { Container } from "aurelia-dependency-injection";
import $ from "jquery";
import moment from "moment";
import GoogleMaps = google.maps;
import eventBus from "../../../../../utilities/eventBus";
import { AnalyticsCategory, AnalyticsService } from "../../../../../services/analytics.service";
import { AgencyService } from "./../../../../../services/agency.service";
import { CalendarMapService } from "../../../../../services-vue/calendar-map-service";
import {
    GeolocationService,
    GeoLocationEvents,
    ensureLocationData,
    hasFiniteCoords,
    requestDeviceLocation
} from "../../../../../services-vue/geolocation.service";
import { TaskService } from "../../../../../services/task.service";
import { ProductService } from "../../../../../services/product.service";
import { ToastrService } from "../../../../../services/toastr.service";
import { IVisitMapDetails } from "../../../../../resources-vue/vue-interfaces/i-visit-map";
import { ILocation } from "../../../../../resources-vue/vue-interfaces/i-location";
import { IAddCalendarMarkerOptions } from "../../../../../resources-vue/vue-interfaces/i-calendar-map";
import {
    ApplicationEnum,
    ParentPermissionEnum,
    PermissionActionEnum,
    EvvStatusHomeCareEnum,
    EvvStatusHomeHealthEnum
} from "../../../../../enums/enums";
import {
    formatFullAddress,
    formatScheduledTime,
    formatTimeInOut
} from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import {
    getProductPillClass,
    getProductFullName,
    getApplicationId
} from "../../../../../common/vue-helpers/modifiers/application-modifiers";
import { GetZipEvents } from "../../../../../resources-vue/vue-dialogs/GetZip/GetZip";

import ReassignVisit from "../../../../../resources-vue/vue-dialogs/ReassignVisit/ReassignVisit.vue";
import ProductPill from "../../../../../resources-vue/vue-elements/ProductPill/ProductPill.vue";
import MultiSelectFilter from "../../../../../resources-vue/vue-elements/MultiSelectFilter/MultiSelectFilter.vue";
import FindNearbyEmployees from "../FindNearbyEmployees/FindNearbyEmployees.vue";
import VisitDetails from "../../../../../resources-vue/vue-dialogs/VisitDetails/VisitDetails.vue";
import GetZip from "../../../../../resources-vue/vue-dialogs/GetZip/GetZip.vue";

export default defineComponent({
    components: {
        ProductPill,
        MultiSelectFilter,
        FindNearbyEmployees
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    props: {
        mapVisitsList: {
            type: Array as PropType<IVisitMapDetails[]>,
            default: []
        },
        date: { type: String, default: moment().format("MM/DD/YYYY") },
        weekDayRange: { type: Number, default: 1 },
        isPremiumByAgency: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            _myMarker: null as GoogleMaps.Marker,
            _bounds: null as GoogleMaps.LatLngBounds,
            _map: null as GoogleMaps.Map,
            _agencyService: null as AgencyService,
            _prodService: null as ProductService,
            _analyticsService: null as AnalyticsService,
            _calendarMapService: null as CalendarMapService,
            _permissionManager: null as PermissionManager,
            _taskService: null as TaskService,
            _geolocationService: null as GeolocationService,
            _locationData: {} as ILocation,
            _toastrService: null,
            _locations: {} as any,
            _createdMarkerCount: 0,
            _totalLocations: 0,
            locationsFromCoords: {} as any,
            locationsFromAddress: {} as any,
            currentInfoWindow: null,
            allLocationCoords: [],
            addressMapping: {} as any,
            highlightPatientNameList: [],
            highlightLocationsList: [],
            isReassignAllowed: false as boolean,
            isFindNearbyEmpAllowed: false as boolean,
            dialogRef: null,
            selectedVisitDetails: {} as any,
            showNearbyEmpPanel: false as boolean,
            showLoading: true as boolean,
            patientInfoWindow: "" as string,
            canShowPremium: false as boolean,
            zipNotEntered: false as boolean,
            getApplicationId
        };
    },

    emits: ["mapRefresh", "update:hideFilters"],

    async created() {
        this._agencyService = Container.instance.get(AgencyService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this._calendarMapService = Container.instance.get(CalendarMapService);
        this._taskService = Container.instance.get(TaskService);
        this._prodService = Container.instance.get(ProductService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._toastrService = Container.instance.get(ToastrService);
        this._geolocationService = Container.instance.get(GeolocationService);
        this.initSubscriptions();
        await this._init();
        eventBus.on("highlightPatientNameMarker", async (selectedPatients) => {
            this.highlightPatientNameList = [];
            this.getHighlightsFromPatientName(selectedPatients, this.locationsFromCoords);
            this.getHighlightsFromPatientName(selectedPatients, this.locationsFromAddress);
            this._clearLocationMarkers(this.locationsFromCoords);
            this._clearLocationMarkers(this.locationsFromAddress);
            this.allLocationCoords = [];
            this._markLocations(this.locationsFromCoords);
            this._markLocations(this.locationsFromAddress);
        });
        eventBus.on("highlightPatientLocationMarker", async (selectedLocations) => {
            this.highlightLocationsList = [];
            this.getHighlightsFromAddress(selectedLocations);
            this._clearLocationMarkers(this.locationsFromCoords);
            this._clearLocationMarkers(this.locationsFromAddress);
            this.allLocationCoords = [];
            this._markLocations(this.locationsFromCoords);
            this._markLocations(this.locationsFromAddress);
        });
    },

    async mounted() {
        this.isReassignAllowed = this._permissionManager.checkPermission(
            ParentPermissionEnum.clinician,
            PermissionActionEnum.canReassign
        );
        this.isFindNearbyEmpAllowed = this._permissionManager.checkPermission(
            ParentPermissionEnum.scheduler,
            PermissionActionEnum.canEdit
        );
    },

    watch: {
        mapVisitsList: {
            handler() {
                this._clearAllLocations(this.locationsFromCoords);
                this._clearAllLocations(this.locationsFromAddress);
                this.highlightLocationsList = [];
                this._init();
            }
        },
        showNearbyEmpPanel: {
            handler(newValue) {
                if (newValue === false) {
                    this.$emit("mapRefresh");
                }
                this.$emit("update:hideFilters", newValue);
            },
            deep: true
        }
    },

    methods: {
        initSubscriptions() {
            eventBus.on(GeoLocationEvents.Retrieved, async () => {
                this._clearAllLocations(this.locationsFromCoords);
                this._clearAllLocations(this.locationsFromAddress);
                await this._init();
            });

            eventBus.on(GetZipEvents.Changed, () => {
                this.zipNotEntered = false;
            });

            eventBus.on(GetZipEvents.Canceled, (isChange: boolean) => {
                if (!isChange) {
                    this.zipNotEntered = true;
                }
            });
        },

        async enterLocation() {
            await this.openGetZipDialog();
        },

        async openGetZipDialog(zip: string = "") {
            let zipOptions = {
                zip: zip
            };
            this.dialogRef = this.$dialog.open(GetZip, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    zipOptions
                }
            });
        },

        async _init(): Promise<void> {
            // Without location data, the map center is NaN and Google Maps throws InvalidValueError.
            if (!ensureLocationData(this._geolocationService, this._locationData)) {
                // Try the device's current location automatically before asking the user for a zip.
                requestDeviceLocation(this._geolocationService, () => {
                    this.zipNotEntered = true;
                });
                return;
            }
            this.zipNotEntered = false;
            await this._calendarMapService.loadMapsApi();
            this._markUser();
            if (this.mapVisitsList.length > 0) {
                this.allLocationCoords = [];
                await this._groupVisitsByLocation();
                this._markLocations(this.locationsFromCoords);
            }
        },

        _markUser(): void {
            try {
                const lat: number = +this._locationData?.lat;
                const lon: number = +this._locationData?.lon;
                if (!hasFiniteCoords(lat, lon)) {
                    console.warn("PatientVisitMap: Skipping map init, no valid location coordinates available.");
                    return;
                }
                this._bounds = this._calendarMapService.getBounds();
                this._map = this._calendarMapService.getMap(document.getElementById("Patient__VisitMap"), {
                    center: {
                        lat: lat,
                        lng: lon
                    },
                    zoom: 15, // Higher is zoom in and lower is zoom out
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                    gestureHandling: "greedy"
                });
                this._myMarker = this._calendarMapService.addMarker({
                    position: {
                        lat: lat,
                        lng: lon
                    },
                    map: this._map,
                    animationType: "drop",
                    type: "user"
                });
                const info = this._calendarMapService.addInfoWindow({
                    content: "<div class='text-center fw-normal py-1'>Your Location</div>"
                });
                // Marker Events
                this._myMarker.addListener("mouseover", () => {
                    info.open(this._map, this._myMarker);
                });
                this._myMarker.addListener("mouseout", () => {
                    info.close();
                });

                this._bounds.extend(this._myMarker.getPosition());
            } catch (error) {
                console.error(error?.message);
            }
        },

        getUpdatedLatLng(lat: any, lng: any): any {
            const latLngStr = lat + "," + lng;
            if (!this.allLocationCoords.includes(latLngStr)) {
                return { lat, lng };
            }
            return this.getUpdatedLatLng(lat, lng + 0.0001);
        },

        updateLocationsList(locations: any, patientName: string, visit: any) {
            const latLngStr = visit.PatientAddress.AddressLatitude + "," + visit.PatientAddress.AddressLongitude;
            if (Object.keys(locations).includes(patientName)) {
                if (Object.keys(locations[patientName]).includes(latLngStr)) {
                    locations[patientName][latLngStr].push(visit);
                } else {
                    locations[patientName][latLngStr] = [visit];
                    this.addressMapping[latLngStr] = formatFullAddress(visit.PatientAddress);
                    this._totalLocations++;
                }
            } else {
                locations[patientName] = { [latLngStr]: [visit] };
                this.addressMapping[latLngStr] = formatFullAddress(visit.PatientAddress);
                this._totalLocations++;
            }
        },

        async _groupVisitsByLocation(): Promise<void> {
            for (const visit of this.mapVisitsList) {
                const currentPatientName =
                    visit.PatientName?.FirstName + " " + visit.PatientName?.LastName + " " + visit.Application;

                const coords = {
                    lat: visit.PatientAddress.AddressLatitude,
                    lng: visit.PatientAddress.AddressLongitude
                };

                if (coords.lat && coords.lng) {
                    this.updateLocationsList(this.locationsFromCoords, currentPatientName, visit);
                } else {
                    const address = formatFullAddress(visit.PatientAddress);
                    if (address && address.length > 3) {
                        await this._calendarMapService.getCoordsFromAddress(address, (newcoords) => {
                            visit.PatientAddress.AddressLatitude = Number(newcoords.lat);
                            visit.PatientAddress.AddressLongitude = Number(newcoords.lng);
                            this.updateLocationsList(this.locationsFromAddress, currentPatientName, visit);
                            this._clearLocationMarkers(this.locationsFromAddress);
                            this._markLocations(this.locationsFromAddress);
                        });
                    }
                }
            }
        },

        getHighlightsFromPatientName(selectedPatients: string[], locations: any) {
            for (const patientName of Object.keys(locations)) {
                if (selectedPatients.includes(patientName)) {
                    this.highlightPatientNameList = [
                        ...new Set([...this.highlightPatientNameList, ...Object.keys(locations[patientName])])
                    ];
                }
            }
        },

        getHighlightsFromAddress(selectedLocations: any[]) {
            for (const [key, value] of Object.entries(this.addressMapping)) {
                if (selectedLocations.includes(value)) {
                    this.highlightLocationsList = [...new Set([...this.highlightLocationsList, key])];
                }
            }
        },

        _markLocations(locations: any): void {
            if (this._map && locations && !$.isEmptyObject(locations)) {
                for (const patientName of Object.keys(locations)) {
                    for (const locs of Object.keys(locations[patientName])) {
                        const patientVisits = locations[patientName][locs];
                        const image = require(`../../../../../assets/images/client-pin.svg`);
                        const highlightPin: boolean = [
                            ...this.highlightLocationsList,
                            ...this.highlightPatientNameList
                        ].includes(locs);
                        const icon = highlightPin
                            ? { url: image, scaledSize: new google.maps.Size(60, 86) }
                            : { url: image };

                        const [lat, lng] = locs.split(",");

                        let coords = {
                            lat: Number(lat),
                            lng: Number(lng)
                        };
                        let marker = null;
                        let infoWindow = null;

                        const content = this.getPatientInfoWindowContent(patientVisits);
                        if (!!coords.lat && !!coords.lng) {
                            try {
                                if (this.allLocationCoords.includes(coords.lat + "," + coords.lng)) {
                                    // modifying marker coords by small value, to display multiple markers with same coords.
                                    const res = this.getUpdatedLatLng(coords.lat, coords.lng + 0.0001);
                                    coords = {
                                        lat: res.lat,
                                        lng: res.lng
                                    };
                                }
                                const options: IAddCalendarMarkerOptions = {
                                    position: coords,
                                    map: this._map,
                                    icon: icon
                                };
                                this._bounds.extend(coords);
                                marker = this._calendarMapService.addMarker(options);
                                infoWindow = this._calendarMapService.createInfoWindow(content);
                                // keeps the track of all the marked locations
                                this.allLocationCoords.push(coords.lat + "," + coords.lng);
                                this._analyticsService.logEvent({
                                    category: "Create-Marker",
                                    action: "Coordinates"
                                });
                            } catch (error) {
                                console.error(error);
                            }
                        }
                        locations[patientName][locs].marker = marker;
                        this.initMarkerEvents(marker, infoWindow, locations[patientName][locs]);
                    }
                }
            }
        },

        initMarkerEvents(markerInstance: GoogleMaps.Marker, infoWindow: any, visits: any) {
            if (markerInstance) {
                markerInstance.addListener("click", () => {
                    if (this.currentInfoWindow != null) {
                        this.currentInfoWindow.close();
                        this._analyticsService.logEvent({
                            category: AnalyticsCategory.CentralCalendarNavigation,
                            action: "Central Patient calendar Map visit info Close"
                        });
                    }
                    infoWindow.open(this._map, markerInstance);
                    this.currentInfoWindow = infoWindow;
                    this._analyticsService.logEvent({
                        category: AnalyticsCategory.CentralCalendarNavigation,
                        action: "Central Patient calendar Map visit info Open"
                    });
                });

                // storing the reference, as reference changes in google map events
                const thisReference = this;

                google.maps.event.addListener(infoWindow, "domready", (x) => {
                    document.querySelectorAll(".dropdown-reassign").forEach((el) => {
                        el.addEventListener("click", () => {
                            const visitId = el.id.split("_")[2];
                            this.selectedVisitDetails = visits.find((v: any) => v.Id == visitId);
                            thisReference.scheduledVisit();
                            this._analyticsService.logEvent({
                                category: AnalyticsCategory.CentralCalendarNavigation,
                                action: "Central Patient calendar Map VisitReassign"
                            });
                        });
                    });

                    document.querySelectorAll(".dropdown-nearby-emp").forEach((el) => {
                        el.addEventListener("click", async () => {
                            const visitId = el.id.split("_")[3];
                            this.selectedVisitDetails = visits.find((v: any) => v.Id == visitId);
                            const name: any =
                                this.selectedVisitDetails.PatientName.FirstName +
                                " " +
                                this.selectedVisitDetails.PatientName.LastName;
                            const address: any = formatFullAddress(this.selectedVisitDetails.PatientAddress);
                            this.showNearbyEmpPanel = true;
                            this._analyticsService.logEvent({
                                category: AnalyticsCategory.CentralCalendarNavigation,
                                action: "Central Patient calendar Map visit FindNearByEmployees"
                            });
                            this.patientInfoWindow = this.getPatientInfoWindowContent([this.selectedVisitDetails]);
                            eventBus.emit("selectedVisitForNearbyEmp", {
                                agencyName: this.selectedVisitDetails.AgencyName,
                                patientName: name,
                                patientAddr: address,
                                visitName: this.selectedVisitDetails.VisitName,
                                patientStatus: this.selectedVisitDetails.PatientStatus
                            });
                        });
                    });

                    document.querySelectorAll(".dropdown-details").forEach((el) => {
                        el.addEventListener("click", () => {
                            let visitId = el.id.split("_")[2];
                            let currentVisitDetails = visits.find((v: any) => v.Id === visitId);
                            thisReference.showVisitDetails(currentVisitDetails);
                            this._analyticsService.logEvent({
                                category: AnalyticsCategory.CentralCalendarNavigation,
                                action: "Central Patient calendar Map VisitDetails"
                            });
                        });
                    });
                });

                this._createdMarkerCount++;
                if (this._createdMarkerCount === this._totalLocations) {
                    this._map.fitBounds(this._bounds);
                }
            }
        },

        _clearAllLocations(locations: any): void {
            for (const patientName of Object.keys(locations)) {
                for (const locs of Object.keys(locations[patientName])) {
                    const currentMarker = locations[patientName][locs].marker;
                    if (currentMarker) {
                        toRaw(currentMarker).setMap(null);
                    }
                }
                delete locations[patientName];
            }
            this._totalLocations = 0;
            this._createdMarkerCount = 0;
        },

        _clearLocationMarkers(locations: any) {
            for (const patientName of Object.keys(locations)) {
                for (const locs of Object.keys(locations[patientName])) {
                    const currentMarker = locations[patientName][locs].marker;
                    if (currentMarker) {
                        toRaw(currentMarker).setMap(null);
                    }
                }
            }
        },

        getPatientInfoWindowContent(visits: any): string {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralCalendarNavigation,
                action: "Central Patient calendar Map visit info"
            });
            let patientVisits: string = "";
            const patientName = visits[0].PatientName;
            let tempApplicationId: ApplicationEnum;
            if (visits[0].Application === 256) {
                tempApplicationId = ApplicationEnum.AxxessHospice;
            } else if (visits[0].Application === 65536) {
                tempApplicationId = ApplicationEnum.AxxessPalliative;
            } else {
                tempApplicationId = visits[0].Application;
            }

            for (const v of visits) {
                patientVisits += this.getVisitDetails(v);
            }

            return ` <div id="${visits[0].UserId}" class="visit-marker-details">
                <span class="${getProductPillClass(tempApplicationId)} pill">
                    ${getProductFullName(tempApplicationId)}
                </span>
                <big class="info-header"><b>${patientName.FirstName} ${patientName.LastName}</b></big>
                ${patientVisits}
                <div class="visit-address">${formatFullAddress(visits[0].PatientAddress)}</div>
                </div>`;
        },

        getVisitDetails(visit: any) {
            const visitTime = formatTimeInOut(
                formatScheduledTime(visit.StartDateTime),
                formatScheduledTime(visit.EndDateTime)
            );

            let evvHouse = null;
            if (visit.Application == ApplicationEnum.HomeCare) {
                if (
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.Finished ||
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                ) {
                    evvHouse =
                        visit.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                            ? `<a class='evv'><i class='evv-adjusted signed'></i></a>`
                            : `<a class='evv'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgress ||
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                ) {
                    evvHouse =
                        visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                            ? `<a class='evv'><i class='evv-adjusted in-progress'></i></a>`
                            : `<a class='evv'><i class='fa fa-home fa-md'></i></a>`;
                } else if (visit.EvvIconStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            } else if (visit.Application == ApplicationEnum.AgencyCore) {
                if (visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.InProgress) {
                    evvHouse = `<a class='evv ps-1'><i class='fa fa-home fa-md'></i></a>`;
                } else if (
                    visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.PatientVerified ||
                    visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.ClinicianVerified
                ) {
                    evvHouse = `<a class='evv'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            }
            const showReassignBtn = ["not yet due", "not yet started"].includes(visit.Status.toLowerCase());
            return `<div class="visit-details pe-5">
                        <div class="fw-normal">${visit.VisitName}</div>
                        <div>${visit.Status}</div>
                        <div>${visitTime}</div>
                        <div>Scheduled with ${visit.EmployeeName.FirstName + " " + visit.EmployeeName.LastName}</div>
                        <div class="evvHouse">${evvHouse ? evvHouse : ""}</div>
                        <span class="table-btn-group  ms-auto btn-reassign" id = "edit-icon-${visit.Id}">
                            <button type="button" class="border-0 btn px-1 py-0 btn-outline-info dropdown-toggle" data-bs-toggle="dropdown" data-bs-display="static" id = "edit-btn-${
                                visit.Id
                            }">
                                <i class="fas fa-edit"></i>
                            </button>

                            <div class="${
                                visit.Application === ApplicationEnum.AgencyCore
                                    ? "dropdown-menu"
                                    : "dropdown-menu multiple-items"
                            }">
                                ${
                                    this.isReassignAllowed && showReassignBtn && !this.showNearbyEmpPanel
                                        ? `<a class="dropdown-item dropdown-reassign" href="javascript:void(0);" id = "btn_reassign_${
                                              visit.Id
                                          }">Reassign</a>
                                        ${
                                            this.isPremiumByAgency[visit.AgencyId]
                                                ? `<a class="dropdown-item dropdown-nearby-emp border-top near-ai-button" href="javascript:void(0);" id = "btn_nearby_emp_${visit.Id}">Find Nearby Employees</a>`
                                                : `<div class="dropdown-item border-top near-ai-button ai-disabled"
                                            id = "btn_nearby_emp_${
                                                visit.Id
                                            }">Find Nearby Employees <img src="${require("../../../../../assets/images/lock.svg")}" />
                                            <span class="locked-premium-tooltip">Contact Axxess to unlock this <br /> premium Axxess intelligence feature.</span></div>`
                                        }`
                                        : ""
                                }
                                <a class="dropdown-item dropdown-details" href="javascript:void(0);" id = "btn_details_${
                                    visit.Id
                                }">Details</a>
                            </div>
                        </span>
                    </div>`;
        },

        async scheduledVisit() {
            this.selectedVisitDetails.TaskName = this.selectedVisitDetails.VisitName;
            this.selectedVisitDetails.UserFirstName = this.selectedVisitDetails.EmployeeName.FirstName;
            this.selectedVisitDetails.UserLastName = this.selectedVisitDetails.EmployeeName.LastName;
            this.selectedVisitDetails.FirstName = this.selectedVisitDetails.PatientName.FirstName;
            this.selectedVisitDetails.LastName = this.selectedVisitDetails.PatientName.LastName;

            this.dialogRef = this.$dialog.open(ReassignVisit, {
                props: {
                    modal: true,
                    showHeader: false,
                    contentStyle: {
                        minWidth: "650px",
                        minHeight:
                            this.selectedVisitDetails.Application === ApplicationEnum.HomeCare ? "350px" : "280px"
                    }
                },
                data: {
                    visitDetails: this.selectedVisitDetails
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        this.$emit("mapRefresh");
                    }
                }
            });
        },

        async showVisitDetails(visitDetails: any) {
            visitDetails.TaskName = visitDetails.VisitName;
            visitDetails.UserFirstName = visitDetails.EmployeeName.FirstName;
            visitDetails.UserLastName = visitDetails.EmployeeName.LastName;
            visitDetails.FirstName = visitDetails.PatientName.FirstName;
            visitDetails.LastName = visitDetails.PatientName.LastName;

            this.dialogRef = this.$dialog.open(VisitDetails, {
                props: { modal: true, showHeader: false },
                data: {
                    visitDetails
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        this.$emit("mapRefresh");
                    }
                }
            });
        }
    }
});
